import type { Catalog, JsonObject } from "./types.js";

const SUPPORTED_PROTOCOL_VERSION = 1;

export interface Q5MClientOptions {
  apiUrl: string;
  apiKey: string;
  groupId?: string;
  fetch?: typeof globalThis.fetch;
  timeoutMs?: number;
}

export class Q5MError extends Error {
  readonly status: number;
  readonly code: string;
  readonly result?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code = "request_failed",
    result?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "Q5MError";
    this.status = status;
    this.code = code;
    this.result = result;
  }
}

function errorDetails(payload: unknown, status: number): Q5MError {
  const root = payload !== null && typeof payload === "object" ? payload : {};
  const detailValue = "detail" in root ? root.detail : root;
  const detail = detailValue !== null && typeof detailValue === "object" ? detailValue : {};
  const message =
    "error" in detail && typeof detail.error === "string"
      ? detail.error
      : typeof detailValue === "string"
        ? detailValue
        : `q5m request failed with HTTP ${status}`;
  const code =
    "code" in detail && typeof detail.code === "string" ? detail.code : "request_failed";
  return new Q5MError(message, status, code);
}

export class Q5MClient {
  readonly apiUrl: string;
  readonly groupId?: string;
  readonly #apiKey: string;
  readonly #fetch: typeof globalThis.fetch;
  readonly #timeoutMs?: number;

  constructor(options: Q5MClientOptions) {
    this.apiUrl = options.apiUrl.replace(/\/+$/, "");
    this.#apiKey = options.apiKey;
    this.groupId = options.groupId;
    this.#fetch = options.fetch ?? globalThis.fetch;
    this.#timeoutMs = options.timeoutMs;
  }

  async getCatalog(signal?: AbortSignal): Promise<Catalog> {
    const payload = await this.#request("/cli/v1/catalog", undefined, signal);
    if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
      throw new Q5MError("q5m returned an invalid tool catalog", 200, "invalid_response");
    }
    const record = payload as Record<string, unknown>;
    if (record.protocol_version !== SUPPORTED_PROTOCOL_VERSION) {
      throw new Q5MError(
        `Unsupported q5m CLI protocol version: ${String(record.protocol_version)}`,
        200,
        "unsupported_protocol",
      );
    }
    if (
      typeof record.instructions !== "string" ||
      !Array.isArray(record.tools) ||
      record.agent === null ||
      typeof record.agent !== "object" ||
      Array.isArray(record.agent)
    ) {
      throw new Q5MError("q5m returned an invalid tool catalog", 200, "invalid_response");
    }
    return payload as Catalog;
  }

  async callTool(
    name: string,
    arguments_: JsonObject = {},
    signal?: AbortSignal,
  ): Promise<unknown> {
    return this.#request(
      `/cli/v1/tools/${encodeURIComponent(name)}`,
      {
        method: "POST",
        body: JSON.stringify(arguments_),
      },
      signal,
    );
  }

  async #request(
    path: string,
    init: RequestInit | undefined,
    callerSignal: AbortSignal | undefined,
  ): Promise<unknown> {
    const timeoutSignal =
      this.#timeoutMs === undefined ? undefined : AbortSignal.timeout(this.#timeoutMs);
    const signal =
      callerSignal && timeoutSignal
        ? AbortSignal.any([callerSignal, timeoutSignal])
        : (callerSignal ?? timeoutSignal);
    let response: Response;
    let text: string;
    try {
      response = await this.#fetch(`${this.apiUrl}${path}`, {
        ...init,
        signal,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${this.#apiKey}`,
          ...(init?.body ? { "Content-Type": "application/json" } : {}),
          ...(this.groupId ? { "X-Q5M-Group-Id": this.groupId } : {}),
          ...init?.headers,
        },
      });
      text = await response.text();
    } catch (error) {
      if (error instanceof Q5MError) throw error;
      const message = error instanceof Error ? error.message : String(error);
      throw new Q5MError(`Cannot reach q5m at ${this.apiUrl}: ${message}`, 0, "network_error");
    }
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch (error) {
        if (response.ok) {
          throw new Q5MError(
            `q5m returned invalid JSON: ${(error as Error).message}`,
            response.status,
            "invalid_response",
          );
        }
      }
    }
    if (!response.ok) throw errorDetails(payload, response.status);
    return payload;
  }
}
